import { html, css } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import { SsoElement } from '../components/sso-base.js';
import { endpoints } from '../api/client.js';
import type { SsoTextField } from '../components/sso-text-field.js';
import '../components/sso-card.js';
import '../components/sso-text-field.js';
import '../components/sso-button.js';
import '../components/sso-alert.js';

/**
 * `<forgot-password-page>` — request a reset link (SSO-MOCKUPS.md §9a).
 *
 * The response is ALWAYS a neutral confirmation regardless of whether the account exists
 * (no enumeration, M4). The server sends the mail via the existing Mail system.
 */
@customElement('forgot-password-page')
@localized()
export class ForgotPasswordPage extends SsoElement {
  static override styles = [
    SsoElement.styles,
    css`
      form {
        display: grid;
        gap: var(--sso-space);
      }
      .back {
        text-align: center;
        margin-top: var(--sso-space);
        font-size: 0.9rem;
      }
    `,
  ];

  @state() private busy = false;
  @state() private sent = false;
  @query('sso-text-field') private field?: SsoTextField;

  override firstUpdated(): void {
    this.focusFirstControl();
  }

  private async onSubmit(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    const username = (this.field?.value ?? '').trim();
    if (!username) return;
    this.busy = true;
    try {
      await endpoints.forgotPassword(username);
    } catch {
      // Swallow — the response is neutral either way (M4).
    } finally {
      this.busy = false;
      // Always show the same neutral confirmation.
      this.sent = true;
    }
  }

  override render() {
    return html`
      <sso-card>
        <h1>${msg('Forgot password')}</h1>
        ${this.sent
          ? html`
              <sso-alert kind="success">
                ${msg('If an account exists, an email has been sent.')}
              </sso-alert>
              <p class="back"><a href="/login">${msg('Back to sign in')}</a></p>
            `
          : html`
              <p>
                ${msg(
                  'Enter your username or email. If an account exists, we will send a recovery link.',
                )}
              </p>
              <form @submit=${this.onSubmit} novalidate>
                <sso-text-field
                  label=${msg('Username or email')}
                  autocomplete="username"
                  required
                  ?disabled=${this.busy}></sso-text-field>
                <sso-button type="submit" variant="primary" ?busy=${this.busy}>
                  ${msg('Send recovery link')}
                </sso-button>
              </form>
              <p class="back"><a href="/login">${msg('Back to sign in')}</a></p>
            `}
      </sso-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'forgot-password-page': ForgotPasswordPage;
  }
}
